import { Placement } from '@popperjs/core'
import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import classnames from 'classnames'
import * as React from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { getSanitizedTicker } from '../../../app/shared/ticker-sanitizer'
import { ComboBox, useComboBoxState } from '../combo-box'
import { getExtendedDialogState } from '../dialog'
import { IconNameType } from '../icon'
import { InputSize, InputTheme } from '../input'
import { List, ListItem, useListState } from '../list'
import { INSTRUMENT_CATEGORIES } from './constants'
import { SearchError } from './search-error'
import { SearchList } from './search-list'
import { getRedirectForTicker } from './util'

interface SearchProps {
  autoFocus?: boolean
  defaultValue?: string
  defaultPlacement?: Placement
  placeholder?: string
  theme?: keyof typeof InputTheme
  size?: keyof typeof InputSize
  allowCategorySwitching?: boolean
  inputClass?: string
  popoverClass?: string
  chartIndex?: number
  isMatchInputAndBoxWidth?: boolean
  isSearchIcon?: boolean
  isShowAllVisible?: boolean
  isSearchItemLink?: boolean
  rightContent?: JSX.Element | IconNameType
  onTickerSelected?: (ticker: string) => void
}

export function Search({
  isSearchIcon = true,
  isShowAllVisible = true,
  isSearchItemLink = true,
  ...props
}: SearchProps) {
  const { reset } = useQueryErrorResetBoundary()
  const state = useComboBoxState({
    minValueLength: 1,
    inputValue: props.defaultValue,
    placement: props.defaultPlacement ?? 'bottom-start',
  })
  const { isFullyClosed } = getExtendedDialogState(state)
  const listState = useListState({ orientation: 'vertical' })
  const [category, setCategory] = React.useState(0)

  const changeCategory = React.useRef((ev: React.KeyboardEvent) => {
    switch (ev.key) {
      case 'ArrowRight':
        state.first()
        break
      case 'ArrowUp':
        setCategory((state) => (state - 1 + INSTRUMENT_CATEGORIES.length) % INSTRUMENT_CATEGORIES.length)
        break
      case 'ArrowDown':
        setCategory((state) => (state + 1) % INSTRUMENT_CATEGORIES.length)
        break
    }
  })

  /**
   * The search box can be child of a modal/dialog so we need to handle ESC
   * otherwise it would close both the combobox and the parent.
   */
  const onInputKeyDown = React.useCallback(
    (ev: React.KeyboardEvent) => {
      if (ev.key === 'Escape' && state.visible) {
        ev.stopPropagation()
        state.hide()
      }
    },
    // We only use `visible` and `hide` and don’t need the full state
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.visible, state.hide]
  )

  /**
   * Reset to category "All" when the combobox closes
   */
  React.useEffect(() => {
    if (isFullyClosed) {
      setCategory(0)
    }
  }, [isFullyClosed])

  return (
    <ComboBox
      state={state}
      className="h-64 w-full sm:h-auto"
      aria-label="Search"
      inputProps={{
        theme: props.theme,
        size: props.size,
        autoFocus: props.autoFocus,
        placeholder: props.placeholder ?? 'Search symbol',
        autoComplete: 'off',
        autoCorrect: 'off',
        autoCapitalize: 'off',
        maxLength: 100,
        onKeyDown: onInputKeyDown,
        onBlur: (e) => {
          // prevent combobox from setting input value on input blur
          e.preventDefault()
        },
        inputClass: props.inputClass,
        rightContent: props.rightContent,
        ...(isSearchIcon && { leftContent: 'search' }),
      }}
      popoverProps={{
        className: classnames(props.popoverClass, 'sm:px-0 z-dialog', {
          'w-full sm:w-110 px-2': !props.isMatchInputAndBoxWidth,
          'min-w-[340px]': props.isMatchInputAndBoxWidth,
        }),
        style: {
          width: props.isMatchInputAndBoxWidth
            ? state.unstable_referenceRef.current?.getBoundingClientRect().width ?? 340
            : undefined,
          maxWidth: '100vw',
        },
      }}
      onChange={(value) => {
        if (isSearchItemLink) {
          window.location.href = getRedirectForTicker(value)
        } else {
          props.onTickerSelected?.(getSanitizedTicker(value))
          state.setInputValue('')
        }
      }}
      {...(props.chartIndex !== undefined && { inputTestId: `chart-${props.chartIndex}-settings-search-input` })}
    >
      <div className="flex flex-1 flex-col space-y-2 overflow-hidden sm:flex-row sm:space-x-8 sm:space-y-0">
        {props.allowCategorySwitching && (
          <List
            aria-label="Instrument Categories"
            includeBaseStyles={false}
            className="flex flex-row sm:w-20 sm:flex-col"
            state={listState}
            selectedIndex={category}
            onChange={(index) => setCategory(index)}
            onKeyDown={changeCategory.current}
          >
            {INSTRUMENT_CATEGORIES.map((category) => (
              <ListItem key={category.name}>{category.name}</ListItem>
            ))}
          </List>
        )}
        {!isFullyClosed && (
          <ErrorBoundary
            FallbackComponent={SearchError}
            onReset={reset}
            resetKeys={[state.currentValue, state.inputValue]}
          >
            <SearchList
              state={state}
              instrument={INSTRUMENT_CATEGORIES[category].instrument}
              chartIndex={props.chartIndex}
              isShowAllVisible={isShowAllVisible}
              isSearchItemLink={isSearchItemLink}
              onTickerSelected={props.onTickerSelected}
            />
          </ErrorBoundary>
        )}
      </div>
    </ComboBox>
  )
}
